import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { ContractorOffer } from 'src/app/models/contractor-offer.model';
import { TimestampPipe } from 'src/app/pipes/timestamp/timestamp.pipe';
import { ContractorOffersService } from 'src/app/services/contractor-offers.service';
import { SignatureService } from 'src/app/services/signature.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Roles } from 'src/app/utility/role';
import { sortByTimestamp } from 'src/app/utility/sort';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    TimestampPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [SignatureService],
})
export class ContractsComponent {
  private contractorOffersService = inject(ContractorOffersService);
  private signatureService = inject(SignatureService);
  private usersService = inject(UserSessionService);

  private contracts$ = this.usersService.getCurrentUser().pipe(
    filter((user) => user != null),
    switchMap((user) => user.role === Roles.superAdmin ? this.contractorOffersService.getList({ status: 'accepted' }) : this.usersService.isPremiumPartner(user) ?
      this.contractorOffersService.getList({ partners: [user.guid, ...user.partners], status: 'accepted' }) :
      this.contractorOffersService.getList({ partner: user.guid, status: 'accepted' })),
    map((items) => items.sort((a, b) => sortByTimestamp(a, b, 'date'))),
    map((items) => items.map((it) => {
      const signature$ = this.signatureService.getByItem(it.guid, it.partner.guid);
      return Object.assign({}, it, { signature$ });
    })),
    switchMap((items) => combineLatest([of(items), combineLatest(items.map((it) => it.signature$))])),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  sub = input(false);

  waitingContracts$ = this.contracts$.pipe(
    map(([items, signatures]) => items.map((it) => {
      const signature = signatures.filter((itt) => itt != null).find((itt) => itt.item === it.guid);
      return Object.assign({}, it, { signature });
    }).filter((it) => it.signature?.status === 'created')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  signedContracts$ = this.contracts$.pipe(
    map(([items, signatures]) => items.map((it) => {
      const signature = signatures.filter((itt) => itt != null).find((itt) => itt.item === it.guid);
      return Object.assign({}, it, { signature });
    }).filter((it) => it.signature?.status === 'signed')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  role = computed(() => this.usersService.role());

  onContract(contract: ContractorOffer) {
    Browser.open({ url: contract.contractUrl });
  }
}
